import React, { memo, useCallback, useContext, useMemo, useState } from 'react';

import {
  Modal,
  Button,
  DatePicker,
  Select,
  Col,
  Row,
  message,
  Form,
} from 'antd';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { getFirstAndLastDayOfMonth } from '@utils/formatters';
import api from '@services/api';
import { useNavigate } from 'react-router-dom';
import SelectCustomers from '@components/SelectCustomers';
import SessionContext from '@providers/Session/SessionContext';

interface IDate {
  initialDate: string | undefined;
  endDate: string | undefined;
}

interface IParams extends IDate {
  customerId: number | undefined;
  status: 'O' | 'C' | undefined;
}

interface IRequestInvoiceReport {
  visible: boolean;
  statusOptions: any;
  onClose: Function;
}

const RequestInvoiceReport = ({
  visible,
  statusOptions,
  onClose,
}: IRequestInvoiceReport) => {
  const { t, i18n } = useTranslation('invoices');
  const {
    customer: { customersSelectOptions },
  } = useContext(SessionContext);
  const navigate = useNavigate();
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const requestReport = useCallback(
    (params: IParams) =>
      api.get(`/service-proxy/broker/invoices-report?locale=${i18n.language}`, {
        params,
      }),
    [i18n],
  );

  const handleClose = useCallback(() => {
    onClose();
    form.resetFields();
  }, [form, onClose]);

  const handleChangeDate = useCallback((date: Moment): IDate => {
    const newDate: IDate = {
      initialDate: undefined,
      endDate: undefined,
    };
    if (date) {
      const { firstDay, lastDay } = getFirstAndLastDayOfMonth(date.toDate());
      newDate.initialDate = firstDay;
      newDate.endDate = lastDay;
    }
    return newDate;
  }, []);

  const navigateToReports = useCallback(() => {
    message.destroy('invoice-report');
    navigate(`/support/reports?type=financial`);
  }, [navigate]);

  const handleRequestReport = useCallback(async () => {
    try {
      setLoadingRequest(true);

      const { date, ...values } = formValues;

      const params: IParams = {
        ...values,
        ...handleChangeDate(date),
      };
      await requestReport(params);
      message.success({
        content: (
          <span>
            {t('report.msgs.request-success')}
            <Button
              onClick={navigateToReports}
              type="link"
              style={{ padding: 4 }}
            >
              {t('report.msgs.go-to')}
            </Button>
            {t('report.msgs.check-progress-and-download')}
          </span>
        ),
      });
    } catch {
      message.error(t('report.msgs.check-progress-and-download'));
    }
    handleClose();
    setLoadingRequest(false);
  }, [
    formValues,
    handleChangeDate,
    handleClose,
    navigateToReports,
    requestReport,
    t,
  ]);

  const modalInputs = useMemo(
    () => [
      {
        showItem: true,
        name: 'customerId',
        label: t('search.customer'),
        item: <SelectCustomers options={customersSelectOptions} allowClear />,
      },
      {
        showItem: true,
        name: 'status',
        label: t('search.status'),
        item: (
          <Select
            options={statusOptions}
            style={{ width: '100%' }}
            allowClear
            showSearch
          />
        ),
      },
      {
        name: 'date',
        label: t('search.datesRange'),
        showItem: formValues?.status === 'C',
        item: (
          <DatePicker
            format="MMMM/YYYY"
            style={{ width: '100%' }}
            disabledDate={d => {
              return d && d >= moment().startOf('month');
            }}
            picker="month"
          />
        ),
      },
    ],
    [formValues?.status, customersSelectOptions, statusOptions, t],
  );

  return (
    <Modal
      title={t('report.request')}
      open={visible}
      width="70%"
      style={{
        maxWidth: 900,
      }}
      centered
      onCancel={handleClose}
      afterClose={() => document.body.style.removeProperty('overflow')}
      footer={
        <>
          <Button onClick={handleClose}>{t('report.buttons.cancel')}</Button>
          <Button
            type="primary"
            loading={loadingRequest}
            disabled={loadingRequest}
            onClick={handleRequestReport}
          >
            {t('report.buttons.request')}
          </Button>
        </>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]} style={{ padding: '0 16px' }}>
          {modalInputs?.map(
            ({ name, label, item, showItem }) =>
              showItem && (
                <Col key={label} lg={8} xl={8} xs={24}>
                  <Form.Item name={name} label={label}>
                    {item}
                  </Form.Item>
                </Col>
              ),
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default memo(RequestInvoiceReport);
